import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Flame } from "lucide-react";
import Layout from "../components/Layout";
import Breadcrumbs from "../components/Breadcrumbs";
import QuoteForm from "../components/QuoteForm";
import PreFooterCTA from "../components/PreFooterCTA";
import { BUSINESS, SERVICE_AREAS } from "../data/business";

const BREADCRUMBS = [{ label: "Contact" }];

// =============================================================================
// ContactHero
// -----------------------------------------------------------------------------
// Desktop (lg+): full 2-col layout — intro text + CTAs on left, GHL form on right.
// Mobile      : the form is the primary thing on the page (the sticky "Call Now"
//               CTA already handles the phone-call path), so we collapse the
//               intro to a single big H1 ("Get a Free Quote") and push the form
//               immediately below it. The longer "get in touch" intro + phone /
//               email / hours buttons are moved into the section below the form
//               on mobile.
// =============================================================================
function ContactHero() {
  return (
    <section className="relative isolate overflow-hidden text-white lg:min-h-[calc(100vh-100px)] lg:flex lg:items-center">
      <div className="absolute inset-0 -z-10 bg-[#1A1D21]" />
      <div className="absolute inset-0 -z-10 abc-grid-pattern opacity-30" />

      <div className="abc-container w-full py-8 lg:py-12 grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        {/* MOBILE: short title + form first. DESKTOP: full intro + CTAs left, form right. */}
        <div className="lg:col-span-7">
          <Breadcrumbs items={BREADCRUMBS} />

          {/* Mobile-only compact title */}
          <h1
            className="font-display mt-3 lg:hidden text-[28px] leading-[1.1] font-extrabold"
            data-testid="contact-h1-mobile"
          >
            Get a <span className="text-[#E31D2B]">Free Quote</span>
          </h1>
          <p className="mt-2 lg:hidden text-[14px] text-white/80" data-testid="contact-subhead-mobile">
            Tell us what&apos;s going on — we answer 24/7, day or night.
          </p>

          {/* Desktop-only full intro + CTAs */}
          <h1
            className="font-display mt-3 hidden lg:block text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight"
            data-testid="contact-h1"
          >
            Get in touch with <span className="text-[#E31D2B]">Drain Sights</span>
          </h1>
          <p className="mt-4 hidden lg:block text-white/85 max-w-xl text-base md:text-lg">
            Call, email, or send us a quick note. A real person answers 24/7 — day or night, weekends and holidays included.
          </p>

          <div className="mt-5 hidden lg:flex flex-wrap gap-3">
            <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent text-base py-3.5 px-5" data-testid="contact-hero-call">
              <Phone className="w-5 h-5" /> Call {BUSINESS.phone}
            </a>
            <a href={BUSINESS.emailHref} className="abc-btn abc-btn-ghost-light text-base py-3.5 px-5" data-testid="contact-hero-email">
              <Mail className="w-5 h-5" /> Email us
            </a>
          </div>

          <div className="mt-5 hidden lg:flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#E31D2B]" /> {BUSINESS.hours.weekdays}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#54616E]" /> {BUSINESS.address}</span>
          </div>
        </div>

        <div className="lg:col-span-5 mt-3 lg:mt-0">
          <QuoteForm source="contact-page" />
        </div>
      </div>
    </section>
  );
}

function ContactDetails() {
  return (
    <div className="abc-card p-6">
      <h3 className="font-display font-bold text-lg text-[#1A1D21]">Contact details</h3>
      <div className="mt-4 space-y-3 text-sm">
        <a href={BUSINESS.phoneHref} className="flex items-center gap-3 text-[#1A1D21] hover:text-[#54616E]" data-testid="contact-phone">
          <Phone className="w-5 h-5 text-[#E31D2B]" /> {BUSINESS.phone}
        </a>
        <a href={BUSINESS.emailHref} className="flex items-center gap-3 text-[#1A1D21] hover:text-[#54616E] break-all" data-testid="contact-email">
          <Mail className="w-5 h-5 text-[#E31D2B] flex-shrink-0" /> {BUSINESS.email}
        </a>
        <div className="flex items-start gap-3">
          <MapPin className="w-5 h-5 text-[#E31D2B] mt-0.5 flex-shrink-0" /> <span>{BUSINESS.address}</span>
        </div>
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-[#E31D2B] mt-0.5 flex-shrink-0" />
          <span>
            <span className="font-semibold text-[#1A1D21]">{BUSINESS.hours.display}</span>
            <br />
            <span>{BUSINESS.hours.detail}</span>
            <meta itemProp="openingHours" content={BUSINESS.hours.schema} />
          </span>
        </div>
      </div>
    </div>
  );
}

// Drain/sewer problem framing — business-hours positioning.
function EmergencyCard() {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#1A1D21] to-[#262A30] text-white p-6 flex gap-4 items-start" data-testid="contact-featured">
      <Flame className="w-8 h-8 text-[#E31D2B] flex-shrink-0" />
      <div>
        <h3 className="font-display font-bold text-lg">Drain or sewer problem?</h3>
        <p className="text-white/75 text-sm mt-1">Clogged drain, slow sewer line, or a stubborn backup — call any time, 24/7, and a real person picks up.</p>
        <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent mt-4" data-testid="contact-featured-call">
          <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
        </a>
      </div>
    </div>
  );
}

function ServiceAreaCard() {
  return (
    <div className="abc-card p-6">
      <h3 className="font-display font-bold text-lg text-[#1A1D21] flex items-center gap-2">
        <MapPin className="w-4 h-4 text-[#54616E]" /> Service area
      </h3>
      <ul className="mt-3 grid grid-cols-2 gap-1.5 text-sm">
        {SERVICE_AREAS.map((a) => (
          <li key={a.slug}>
            <Link to={`/service-areas/${a.slug}`} className="text-slate-700 hover:text-[#54616E]">{a.city}, {a.state}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Mobile-only "Other ways to reach us" section. On desktop these CTAs already
// live inside ContactHero, so we hide this block at lg+.
function MobileOtherWays() {
  return (
    <section className="py-8 lg:hidden bg-[#1A1D21] text-white" data-testid="contact-other-ways-mobile">
      <div className="abc-container">
        <h2 className="font-display text-xl font-extrabold">Prefer to talk?</h2>
        <p className="mt-1 text-white/75 text-sm">
          A real person picks up 24/7 — day or night, every day of the week.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-2.5">
          <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent w-full justify-center py-3.5" data-testid="contact-mobile-call">
            <Phone className="w-5 h-5" /> Call {BUSINESS.phone}
          </a>
          <a href={BUSINESS.emailHref} className="abc-btn abc-btn-ghost-light w-full justify-center py-3.5" data-testid="contact-mobile-email">
            <Mail className="w-5 h-5" /> Email us
          </a>
        </div>
        <div className="mt-4 space-y-2 text-sm text-white/85">
          <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#E31D2B]" /> {BUSINESS.hours.weekdays}</div>
          <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#54616E] mt-0.5 flex-shrink-0" /> <span>{BUSINESS.address}</span></div>
        </div>
      </div>
    </section>
  );
}

// Google Maps embed for the Drain Sights Google Business Profile location.
// Renders an interactive map iframe filling the slot beside the contact details.
function GbpEmbedSlot() {
  return (
    <div
      className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50 relative h-full min-h-[460px]"
      data-testid="contact-gbp-map"
      aria-label="Google Maps location for Drain Sights"
    >
      <iframe
        title="Drain Sights location on Google Maps"
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3090.6323122498343!2d-76.6189962!3d39.22851569999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c802c0233ec133%3A0x4937d286f6772ecd!2sDrain%20Sights!5e0!3m2!1sen!2sus!4v1782249138596!5m2!1sen!2sus"
        className="absolute inset-0 w-full h-full"
        style={{ border: 0 }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

export default function Contact() {
  return (
    <Layout>
      <ContactHero />
      <MobileOtherWays />

      <section className="py-10 md:py-14 abc-bg-pale">
        <div className="abc-container grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-6">
            <ContactDetails />
            <EmergencyCard />
            <ServiceAreaCard />
          </div>

          <div className="lg:col-span-7">
            <GbpEmbedSlot />
          </div>
        </div>
      </section>

      <PreFooterCTA title="Need drain or sewer help?" subtitle="A real person picks up 24/7 — day or night." />
    </Layout>
  );
}
